import Tooltip from './Tooltip'
import { useTooltip } from './useTooltip'

export type Column = { label: string; count: number; highlight?: boolean; detail?: string }

const HEIGHT = 150 // plot height in px
const COLUMN_WIDTH = 24 // thin marks: never fill the whole slot

/**
 * Vertical columns of counts with a light y-axis. A highlighted column (e.g. Overdue)
 * uses the alert colour; the rest share one colour.
 */
export default function ColumnChart({ columns, unit = 'project' }: { columns: Column[]; unit?: string }) {
  const { ref, tip, bind } = useTooltip<HTMLDivElement>()
  const ticks = niceTicks(Math.max(...columns.map((c) => c.count), 1))
  const top = ticks[ticks.length - 1]

  return (
    <div className="column-chart" ref={ref}>
      <div className="column-plot" style={{ height: HEIGHT }}>
        {ticks.map((t) => (
          <div key={t} className="gridline" style={{ bottom: (t / top) * HEIGHT }}>
            <span>{t}</span>
          </div>
        ))}
        <div className="columns">
          {columns.map((c) => {
            const tipText = `${c.count} ${unit}${c.count === 1 ? '' : 's'}${c.detail ? ` · ${c.detail}` : ''}`
            return (
              <div key={c.label} className="column-slot" role="img" {...bind(tipText, c.label)}>
                {c.count > 0 && <span className="column-value">{c.count}</span>}
                <span
                  className={`column ${c.highlight ? 'highlight' : ''}`}
                  style={{ height: (c.count / top) * HEIGHT, width: COLUMN_WIDTH }}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className="column-labels">
        {columns.map((c) => (
          <span key={c.label}>{c.label}</span>
        ))}
      </div>
      <Tooltip tip={tip} />
    </div>
  )
}

/** Clean whole-number axis ticks (0, 2, 4, 6…) that cover the largest value. */
function niceTicks(max: number): number[] {
  const step = max <= 3 ? 1 : max <= 12 ? 2 : max <= 30 ? 5 : 10 * Math.ceil(max / 50)
  const ticks = [0]
  while (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step)
  return ticks
}
